import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X, MapPin, Briefcase, Building2, ExternalLink, Users,
  Zap, Tag, History, Sparkles, Loader2, User,
} from "lucide-react";
import type { LinkedInJob, JobStatus } from "../api/LinkedInJobsApi";
import { fetchAlertDetail, STATUS_OPTIONS } from "../api/LinkedInJobsApi";
import { daysAgo, EMPLOYMENT_COLORS, parseJobSummary } from "../utils";
import { STATUS_CHIP_SOLID } from "./JobCard";
import { useLinkedInJobs } from "../modal/LinkedInJobsContext";
import CvEnhancementSection from "./CvEnhancementSection";
import type { CvEnhancementSectionHandle } from "./CvEnhancementSection";
import CoverLetterEnhancementSection from "./CoverLetterEnhancementSection";
import type { CoverLetterEnhancementSectionHandle } from "./CoverLetterEnhancementSection";
import HighlightedText from "shared/HighlightedText";
import TimeAgoChip from "shared/TimeAgoChip";
import RemoteScopeChip from "shared/RemoteScopeChip";
import ColdFusionChip from "shared/ColdFusionChip";
import StatusNotePopover from "shared/StatusNotePopover";
import ReassignPopover from "shared/ReassignPopover";

function formatHistoryDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

interface Props {
  job: LinkedInJob;
  isAdmin: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export default function JobDetailModal({ job, isAdmin, onClose, onSaved }: Props) {
  const { handleStatusUpdate, loadAlertHistory, alertHistory, historyLoading, techManagers, handleReassignAlert } = useLinkedInJobs();

  const jobTechManager = isAdmin && job.employee_id
    ? (techManagers.find((tm) => tm.id === job.employee_id) ?? null)
    : null;

  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyRect, setHistoryRect] = useState<DOMRect | null>(null);

  // Tracking strip state
  const [localStatus, setLocalStatus] = useState<JobStatus>(job.status ?? "new");
  const [pendingStatus, setPendingStatus] = useState<JobStatus | null>(null);
  const [pendingNotes, setPendingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isAlerts = !!job.alert_id;

  const [fullSummary, setFullSummary] = useState<string | null | undefined>(undefined);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [initialEnhancedData, setInitialEnhancedData] = useState<any | null>(null);
  const [initialEnhancedCoverLetterData, setInitialEnhancedCoverLetterData] = useState<any | null>(null);

  useEffect(() => {
    if (!job.alert_id) return;
    setFullSummary(undefined);
    setInitialEnhancedData(null);
    setInitialEnhancedCoverLetterData(null);
    setSummaryLoading(true);
    fetchAlertDetail(job.alert_id, isAdmin)
      .then((d) => {
        setFullSummary(d.job_summary ?? null);
        setInitialEnhancedData(d.enhanced_cv_data ?? null);
        setInitialEnhancedCoverLetterData(d.enhanced_cover_letter_data ?? null);
      })
      .catch(() => setFullSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [job.alert_id, isAdmin]);

  // ── Combined "Enhance All" (CV + Cover Letter) ─────────────────────────────
  const cvSectionRef = useRef<CvEnhancementSectionHandle>(null);
  const clSectionRef = useRef<CoverLetterEnhancementSectionHandle>(null);
  const [selectedCvCategoryId, setSelectedCvCategoryId] = useState<number | null>(null);
  const [cvSelectionId, setCvSelectionId] = useState<number | "">("");
  const [enhancingAll, setEnhancingAll] = useState(false);
  const [hasRunOnce, setHasRunOnce] = useState(false);

  const cvAlreadyEnhanced = !!(initialEnhancedData ?? (job.has_enhancement ? job.enhanced_cv_data : null));
  const clAlreadyEnhanced = !!(initialEnhancedCoverLetterData ?? (job.has_cover_letter_enhancement ? job.enhanced_cover_letter_data : null));
  const showEnhanceAll = !hasRunOnce && !cvAlreadyEnhanced && !clAlreadyEnhanced;

  async function handleEnhanceAll() {
    setHasRunOnce(true);
    setEnhancingAll(true);
    try {
      await Promise.allSettled([
        cvSectionRef.current?.runEnhancement(),
        clSectionRef.current?.canEnhance ? clSectionRef.current.runEnhancement() : Promise.resolve(),
      ]);
    } finally {
      setEnhancingAll(false);
    }
  }

  useEffect(() => {
    if (isAlerts && job.alert_id) {
      loadAlertHistory(job.alert_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.alert_id]);

  function selectStatus(status: JobStatus) {
    if (status === localStatus) {
      setPendingStatus(null);
      setPendingNotes("");
      setSaveError(null);
      return;
    }
    setPendingStatus(status);
    setPendingNotes("");
    setSaveError(null);
  }

  function cancelPending() {
    setPendingStatus(null);
    setPendingNotes("");
    setSaveError(null);
  }

  async function confirmStatusChange() {
    if (!job.alert_id || !pendingStatus) return;
    setSaving(true);
    setSaveError(null);
    try {
      await handleStatusUpdate(job.alert_id, pendingStatus, pendingNotes.trim() || null);
      setLocalStatus(pendingStatus);
      setPendingStatus(null);
      setPendingNotes("");
      onSaved?.();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const displaySummary = isAlerts ? fullSummary : job.job_summary;
  const applyHref = job.apply_link || job.url;
  const employmentColor =
    EMPLOYMENT_COLORS[job.job_employment_type ?? ""] ?? "bg-muted/50 text-muted-foreground border-border/40";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-border shrink-0">
          <div className="w-12 h-12 rounded-xl border border-border bg-surface-muted flex items-center justify-center shrink-0 overflow-hidden">
            {job.company_logo ? (
              <img
                src={job.company_logo}
                alt={job.company_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
                }}
              />
            ) : null}
            <span className={`text-lg font-bold text-primary font-inter ${job.company_logo ? "hidden" : ""}`}>
              {job.company_name.charAt(0).toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter leading-snug">
              {job.job_title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              {job.job_posted_date && (
                <TimeAgoChip label={daysAgo(job.job_posted_date)} />
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                <Building2 className="w-3 h-3" />
                {job.company_url ? (
                  <a href={job.company_url} target="_blank" rel="noreferrer" className="hover:text-primary transition-colors">
                    {job.company_name}
                  </a>
                ) : job.company_name}
              </span>
              {job.job_location && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                  <MapPin className="w-3 h-3" />
                  {job.job_location}
                </span>
              )}
            </div>
            {/* Badges row */}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {job.job_employment_type && (
                <span className={`text-[11px] px-2 py-1 rounded-lg border font-inter font-medium ${employmentColor}`}>
                  {job.job_employment_type}
                </span>
              )}
              {job.job_seniority_level && job.job_seniority_level !== "Not Applicable" && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                  {job.job_seniority_level}
                </span>
              )}
              {job.is_easy_apply && (
                <span className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-inter">
                  <Zap className="w-3 h-3" />
                  Easy Apply
                </span>
              )}
              {job.job_num_applicants > 0 && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-inter">
                  <Users className="w-3 h-3" />
                  {job.job_num_applicants} applicants
                </span>
              )}
              {job.job_industries && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-inter">
                  <Tag className="w-3 h-3" />
                  {job.job_industries.split(",")[0].trim()}
                </span>
              )}
              {!job.is_active && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-red-500/10 text-red-400 border-red-500/20 font-inter">
                  Deactivated
                </span>
              )}
              {isAdmin && jobTechManager && (
                <div className="ml-auto">
                  {job.alert_id != null ? (
                    <ReassignPopover
                      candidates={techManagers}
                      currentEmployeeId={job.employee_id}
                      onReassign={(employeeId) => handleReassignAlert(job.alert_id as number, employeeId)}
                    >
                      <span className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                        <User className="w-3 h-3" />
                        {jobTechManager.name}
                      </span>
                    </ReassignPopover>
                  ) : (
                    <span className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                      <User className="w-3 h-3" />
                      {jobTechManager.name}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <ColdFusionChip show={job.is_coldfusion} className="shrink-0 self-start" />
          <RemoteScopeChip
            remoteScope={job.remote_scope}
            analysisJson={job.remote_worldwide_analysis}
            className="shrink-0 self-start"
          />
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors shrink-0 self-start"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* CV + Cover Letter Enhancement */}
        <div className="relative px-6 py-3 border-b border-border shrink-0 overflow-hidden space-y-2.5">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-violet-500/[0.04] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-primary/70 via-violet-400/40 to-primary/10" />

          {showEnhanceAll && (
            <div className="flex items-center justify-end">
              <button
                onClick={handleEnhanceAll}
                disabled={enhancingAll || cvSelectionId === ""}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
              >
                {enhancingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Enhance All
              </button>
            </div>
          )}

          <CvEnhancementSection
            ref={cvSectionRef}
            job={job}
            isAdmin={isAdmin}
            techManager={jobTechManager}
            initialEnhancedData={initialEnhancedData ?? (job.has_enhancement ? job.enhanced_cv_data : null)}
            jobSummary={fullSummary ?? job.job_summary}
            showOwnTrigger={!showEnhanceAll}
            onCvSelected={(cvId, categoryId) => { setCvSelectionId(cvId); setSelectedCvCategoryId(categoryId); }}
          />
          <CoverLetterEnhancementSection
            ref={clSectionRef}
            job={job}
            isAdmin={isAdmin}
            techManager={jobTechManager}
            initialEnhancedData={initialEnhancedCoverLetterData ?? (job.has_cover_letter_enhancement ? job.enhanced_cover_letter_data : null)}
            autoMatchCategoryId={selectedCvCategoryId}
            groundingCvId={cvSelectionId === "" ? null : cvSelectionId}
            showOwnTrigger={!showEnhanceAll}
          />
        </div>

        {/* Tracking panel — alerts only */}
        {isAlerts && (
          <div className="px-6 pt-3 pb-3 border-b border-border shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter">
                My Tracking
              </span>
              <button
                ref={historyBtnRef}
                onClick={() => {
                  if (historyBtnRef.current) setHistoryRect(historyBtnRef.current.getBoundingClientRect());
                  setShowHistory((v) => !v);
                }}
                className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary font-inter transition-colors underline underline-offset-2"
              >
                <History className="w-3 h-3" />
                History
              </button>
            </div>

            {/* Status pills */}
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => {
                const isCurrent = localStatus === opt.value;
                const isPending = pendingStatus === opt.value;
                return (
                  <StatusNotePopover
                    key={opt.value}
                    note={isCurrent && !isPending ? job.alert_notes : null}
                    fromLabel={job.alert_from_status ? STATUS_CHIP_SOLID[job.alert_from_status]?.label : null}
                    toLabel={opt.label}
                  >
                    <button
                      onClick={() => selectStatus(opt.value as JobStatus)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-all ${
                        isPending
                          ? `${STATUS_CHIP_SOLID[opt.value]?.className ?? "bg-muted text-foreground border-border"} ring-2 ring-white/20 ring-offset-1 ring-offset-surface`
                          : isCurrent
                          ? STATUS_CHIP_SOLID[opt.value]?.className ?? "bg-muted text-foreground border-border"
                          : "bg-transparent text-muted-foreground border-border hover:border-border hover:text-foreground"
                      }`}
                    >
                      {opt.label}
                    </button>
                  </StatusNotePopover>
                );
              })}
            </div>

            {/* Pending confirmation */}
            {pendingStatus && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={pendingNotes}
                  onChange={(e) => setPendingNotes(e.target.value)}
                  placeholder="Add a note for this status change… (optional)"
                  rows={2}
                  autoFocus
                  className="w-full bg-surface-muted/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground font-inter resize-none focus:outline-none focus:border-primary/60 transition-colors"
                />
                {saveError && <p className="text-[11px] text-red-400 font-inter">{saveError}</p>}
                <div className="flex items-center gap-2">
                  <button
                    onClick={confirmStatusChange}
                    disabled={saving}
                    className="px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
                  >
                    {saving ? "Saving…" : `Save as "${STATUS_CHIP_SOLID[pendingStatus]?.label ?? pendingStatus}"`}
                  </button>
                  <button
                    onClick={cancelPending}
                    className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-xs font-inter rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

          </div>
        )}

        {/* History popover — portal so it escapes modal stacking */}
        {isAlerts && showHistory && historyRect && createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={() => setShowHistory(false)} />
            <div
              className="fixed z-[81] w-80 bg-surface-muted border border-border rounded-xl shadow-2xl overflow-hidden"
              style={{ top: historyRect.bottom + 6, left: historyRect.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="text-xs font-semibold text-foreground font-inter">Tracking History</span>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="px-4 py-3 max-h-72 overflow-y-auto">
                {historyLoading ? (
                  <p className="text-xs text-muted-foreground font-inter py-1">Loading…</p>
                ) : alertHistory.length === 0 ? (
                  <p className="text-xs text-muted-foreground font-inter italic py-1">No status changes yet.</p>
                ) : (
                  <div className="space-y-3">
                    {alertHistory.map((h, i) => (
                      <div key={h.id} className="relative pl-4">
                        {i < alertHistory.length - 1 && (
                          <span className="absolute left-[3px] top-3 bottom-[-8px] w-px bg-border" />
                        )}
                        <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-surface-muted" />
                        <div className="flex flex-wrap items-center gap-1.5">
                          {h.from_status && (
                            <>
                              <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${STATUS_CHIP_SOLID[h.from_status]?.className ?? "text-muted-foreground border-transparent"}`}>
                                {STATUS_CHIP_SOLID[h.from_status]?.label ?? h.from_status}
                              </span>
                              <span className="text-muted-foreground text-[10px]">→</span>
                            </>
                          )}
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${STATUS_CHIP_SOLID[h.to_status]?.className ?? "text-muted-foreground border-transparent"}`}>
                            {STATUS_CHIP_SOLID[h.to_status]?.label ?? h.to_status}
                          </span>
                          <span className="ml-auto text-[10px] text-muted-foreground font-inter shrink-0">
                            {formatHistoryDate(h.changed_at)}
                          </span>
                        </div>
                        {h.notes && (
                          <p className="text-[11px] text-muted-foreground font-inter mt-0.5 italic">"{h.notes}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {job.job_function && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-inter mb-4">
              <Briefcase className="w-3.5 h-3.5 shrink-0" />
              {job.job_function}
            </div>
          )}

          {summaryLoading ? (
            <div className="space-y-2.5 animate-pulse">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`h-3 bg-surface-muted rounded ${i % 3 === 2 ? "w-2/3" : "w-full"}`} />
              ))}
            </div>
          ) : displaySummary ? (
            <div className="space-y-3 text-sm font-inter">
              {parseJobSummary(displaySummary).map((block, i) => {
                if (block.type === "heading")
                  return (
                    <h3 key={i} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-4 first:mt-0">
                      {block.text!.replace(/\s*:$/, "").trim()}
                    </h3>
                  );
                if (block.type === "bullets")
                  return (
                    <ul key={i} className="space-y-1.5 pl-1">
                      {block.items!.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-foreground leading-relaxed">
                          <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          <HighlightedText text={item} />
                        </li>
                      ))}
                    </ul>
                  );
                return (
                  <p key={i} className="text-foreground leading-relaxed">
                    {block.text?.split("\n").map((line, j, arr) => (
                      <span key={j}>
                        <HighlightedText text={line} />
                        {j < arr.length - 1 && <br />}
                      </span>
                    ))}
                  </p>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground font-inter italic">No description available.</p>
          )}

        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-border shrink-0">
          <a
            href={applyHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            Apply on LinkedIn
          </a>
        </div>
      </div>
    </div>
  );
}
